import { useEffect, useState } from "react";
import { server } from "../../server";
import axios from "axios";
import { sectionList, subjectList } from "../../utils";

function Toppers() {
  const [toppers, setToppers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const recordsPerPage = 10;
  const [studentClass, setStudentClass] = useState("II");
  const [section, setSection] = useState("A");
  const [subject, setSubject] = useState("English");
  const [toppersType, setToppersType] = useState("Class");

  const classList = [
    "Arun",
    "Uday",
    "I",
    "II",
    "III",
    "IV",
    "V",
    "VI",
    "VII",
    "VIII",
    "IX",
    "X",
  ];

  const fetchToppersDetails = async () => {
    setLoading(true);
    try {
      const payload = {
        topperType: toppersType.toLowerCase(),
        examType: "half yearly",
        class: studentClass,
        session: "2024-2025",
        limit: 5,
      };

      if (toppersType === "Section") payload.section = section;
      if (toppersType === "Subject") payload.subject = subject;

      const response = await axios.post(`${server}/FilterToppers`, payload);

      if (toppersType === "Section") {
        const data = transformSectionData(response.data);
        data.forEach((sec) => {
          sec.toppers.sort((a, b) => b.totalMarks - a.totalMarks);
        });
        setToppers(data);
      } else if (toppersType === "Subject") {
        const data = transformSubjectData(response.data);
        data.forEach((sub) => {
          sub.toppers.sort((a, b) => b.totalMarks - a.totalMarks);
        });
        setToppers(data);
      } else {
        const grouped = response.data.reduce((acc, curr) => {
          const { admission_no, class: cls, section, examData } = curr;

          if (!acc[admission_no]) {
            acc[admission_no] = {
              admission_no,
              class: cls,
              section,
              subjects: [],
              totalMarks: 0,
              maxMarks: 0,
            };
          }

          const marksByHead = examData.marks;

          const score1 = marksByHead["h1"] ?? 0;
          const score2 = marksByHead["h2"] ?? 0;
          const combinedH1H2 = Math.round((score1 + score2) / 4);

          const otherMarks = ["h3", "h4", "h5"].reduce(
            (sum, head) => sum + (marksByHead[head] ?? 0),
            0
          );

          const subjectTotal = combinedH1H2 + otherMarks;

          acc[admission_no].subjects.push({
            subject: examData.subject,
            marks: marksByHead,
            total: subjectTotal,
          });

          acc[admission_no].totalMarks += subjectTotal;
          acc[admission_no].maxMarks += 100;

          return acc;
        }, {});

        // Fetch student details for each topper
        const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        const finalToppers = [];
        for (const topper of Object.values(grouped)) {
          try {
            const studentResponse = await axios.post(
              `${server}/getParticularStudentMongodb`,
              { admission_no: topper.admission_no },
              {
                headers: {
                  Authorization: `${localStorage.getItem("token")}`,
                  "Content-Type": "application/json",
                },
              }
            );

            const studentData =
              studentResponse.data["body-json"]?.body?.student;

            finalToppers.push({
              ...topper,
              name:
                studentData?.name || studentData?.studentDetails?.name || "",
              roll_no:
                studentData?.studentDetails?.rollNo ||
                studentData?.rollNo ||
                "",
              gender: studentData?.gender || "",
              image: studentData?.studentDetails?.studentImage || "",
            });

            await delay(100);
          } catch (err) {
            console.error(
              "Error fetching student details",
              topper.admission_no,
              err
            );
            finalToppers.push(topper);
          }
        }

        finalToppers.sort((a, b) => b.totalMarks - a.totalMarks);
        setToppers(finalToppers);
      }
    } catch (error) {
      console.error("Error fetching toppers:", error);
    } finally {
      setLoading(false);
    }
  };

  const transformSectionData = (sectionData) => {
    return sectionData.map((sec) => {
      const grouped = {};

      sec.results.forEach((item) => {
        const { admission_no, examData } = item;
        const subject = examData.subject;
        const marksByHead = examData.marks;

        const score1 = marksByHead["h1"] ?? 0;
        const score2 = marksByHead["h2"] ?? 0;
        const combinedH1H2 = Math.round((score1 + score2) / 4);

        const otherMarks = ["h3", "h4", "h5"].reduce(
          (sum, head) => sum + (marksByHead[head] ?? 0),
          0
        );

        const subjectTotal = combinedH1H2 + otherMarks;

        if (!grouped[admission_no]) {
          grouped[admission_no] = {
            admission_no,
            section: sec._id,
            subjects: {},
            totalMarks: 0,
            maxMarks: 0, // Add maxMarks
          };
        }

        grouped[admission_no].subjects[subject] = subjectTotal;
        grouped[admission_no].totalMarks += subjectTotal;
        grouped[admission_no].maxMarks += 100; // assuming 100 max per subject
      });

      return {
        section: sec._id,
        toppers: Object.values(grouped),
      };
    });
  };

  const transformSubjectData = (subjectData) => {
    return subjectData.map((sub) => {
      const toppers = sub.results.map((item) => {
        const { admission_no, examData } = item;
        const marksByHead = examData.marks;

        const score1 = marksByHead["h1"] ?? 0;
        const score2 = marksByHead["h2"] ?? 0;
        const combinedH1H2 = Math.round((score1 + score2) / 4);

        const otherMarks = ["h3", "h4", "h5"].reduce(
          (sum, head) => sum + (marksByHead[head] ?? 0),
          0
        );

        const totalMarks = combinedH1H2 + otherMarks;

        return {
          admission_no,
          subject: examData.subject,
          marks: marksByHead,
          totalMarks,
          maxMarks: 100, // assuming 100 max per subject
        };
      });

      return {
        subject: sub._id,
        toppers,
      };
    });
  };

  useEffect(() => {
    setToppers([]);
    if (
      studentClass &&
      ((toppersType === "Section" && section) ||
        (toppersType === "Subject" && subject) ||
        toppersType === "Class")
    ) {
      fetchToppersDetails();
    }
  }, [studentClass, section, subject, toppersType]);

  // Filter
  const filteredRecords = toppers.filter((ComplainBox) => {
    const searchLower = searchTerm.toLowerCase();

    return Object.values(ComplainBox || {}).some(
      (value) =>
        typeof value === "string" && value.toLowerCase().includes(searchLower)
    );
  });

  // Calculate pagination
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredRecords.slice(
    indexOfFirstRecord,
    indexOfLastRecord
  );
  const totalPages = Math.ceil(filteredRecords.length / recordsPerPage);

  // Page Navigation
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div class="container-fluid my-3 px-3">
            <div className="row g-2">
              <div className="col-lg-8">
                <div class="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                  <table class="table mb-0 text-nowrap table-centered">
                    <thead class="table-light">
                      <tr>
                        <th className="w-10">No.</th>
                        <th className="lh-1 w-60">
                          Details
                          <i className="bi bi-info-circle ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Displaying Information as per Record
                          </span>
                        </th>
                        <th className="lh-1 w-30">
                          Action
                          <i className="bi bi-person-gear ms-2 text-dark"></i>
                          <br />
                          <span className="fs-8 fw-normal text-secondary">
                            Role-Specific Action Panel
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* CLASS LEVEL */}
                      {toppersType === "Class" &&
                        currentRecords.map((topper, index) => (
                          <tr key={`${topper.admission_no}-${index}`}>
                            <td>{indexOfFirstRecord + index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Name</span> &mdash;{" "}
                              {topper.name}
                              <br />
                              <span className="text-dark">Roll No.</span>{" "}
                              &mdash; {topper.roll_no}
                              <br />
                              <span className="text-dark">
                                Admission No.
                              </span>{" "}
                              &mdash; {topper.admission_no}
                              <br />
                              <span className="text-dark">Term</span> &mdash;
                              Half Yearly
                              <br />
                              <span className="text-dark">
                                Class
                              </span> &mdash; {topper.class}
                              <br />
                              <span className="text-dark">
                                Section
                              </span> &mdash; {topper.section}
                              <br />
                              <span className="text-dark">
                                Total Marks
                              </span>{" "}
                              &mdash; {topper.totalMarks}
                              <br />
                              <span className="text-dark">
                                Percentage
                              </span> —{" "}
                              {(
                                (topper.totalMarks / topper.maxMarks) *
                                100
                              ).toFixed(2)}
                              %
                              <br />
                              <span className="text-dark">Subjects:</span>
                              <ul className="mb-0">
                                {topper.subjects?.map((sub) => (
                                  <li
                                    key={sub.subject}
                                    className="text-capitalize"
                                  >
                                    {sub.subject}: {sub.total}
                                  </li>
                                ))}
                              </ul>
                            </td>
                          </tr>
                        ))}

                      {/* SECTION LEVEL */}
                      {toppersType === "Section" &&
                        currentRecords.map((sectionItem, idx) =>
                          sectionItem.toppers?.map((topper, index) => (
                            <tr
                              key={`${sectionItem.section}-${topper.admission_no}-${index}-${idx}`}
                            >
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span className="text-dark">Name</span> &mdash;{" "}
                                {topper.name}
                                <br />
                                <span className="text-dark">Roll No.</span>{" "}
                                &mdash; {topper.roll_no}
                                <br />
                                <span className="text-dark">
                                  Admission No.
                                </span>{" "}
                                &mdash; {topper.admission_no}
                                <br />
                                <span className="text-dark">Term</span> &mdash;
                                Half Yearly
                                <br />
                                <span className="text-dark">Section</span>{" "}
                                &mdash; {topper.section}
                                <br />
                                <span className="text-dark">
                                  Total Marks
                                </span>{" "}
                                &mdash; {topper.totalMarks}
                                <br />
                                <span className="text-dark">
                                  Percentage
                                </span> —{" "}
                                {(
                                  (topper.totalMarks / topper.maxMarks) *
                                  100
                                ).toFixed(2)}
                                %
                                <br />
                                <span className="text-dark">Subjects:</span>
                                <ul className="mb-0">
                                  {Object.entries(topper.subjects || {}).map(
                                    ([sub, marks]) => (
                                      <li key={sub} className="text-capitalize">
                                        {sub}: {marks}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </td>
                            </tr>
                          ))
                        )}

                      {/* SUBJECT LEVEL */}
                      {toppersType === "Subject" &&
                        currentRecords.map((subjectItem) =>
                          (subjectItem.toppers || []).map((topper, index) => (
                            <tr
                              key={`${subjectItem.subject}-${topper.admission_no}-${index}`}
                            >
                              <td>{indexOfFirstRecord + index + 1}.</td>
                              <td className="lh-sm py-4 text-wrap">
                                <span className="text-dark">Name</span> &mdash;{" "}
                                {topper.name}
                                <br />
                                <span className="text-dark">Roll No.</span>{" "}
                                &mdash; {topper.roll_no}
                                <br />
                                <span className="text-dark">
                                  Admission No.
                                </span>{" "}
                                &mdash; {topper.admission_no}
                                <br />
                                <span className="text-dark">Term</span> &mdash;
                                Half Yearly
                                <br />
                                <span className="text-dark">Subject</span>{" "}
                                &mdash;{" "}
                                <span className="text-capitalize">
                                  {topper.subject}
                                </span>
                                <br />
                                <span className="text-dark">
                                  Total Marks
                                </span>{" "}
                                &mdash; {topper.totalMarks}
                                <br />
                                <span className="text-dark">
                                  Percentage
                                </span> —{" "}
                                {(
                                  (topper.totalMarks / topper.maxMarks) *
                                  100
                                ).toFixed(2)}
                                %
                                <br />
                                <span className="text-dark">
                                  Marks Breakdown:
                                </span>
                                <ul className="mb-0">
                                  {Object.entries(topper.marks || {}).map(
                                    ([h, marks]) => (
                                      <li key={h}>
                                        {h}: {marks}
                                      </li>
                                    )
                                  )}
                                </ul>
                              </td>
                            </tr>
                          ))
                        )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-lg-4">
                <div class="">
                  <div className="mb-3">
                    <div className="bg-white rounded-pill">
                      <form
                        className="d-flex align-items-center"
                        onSubmit={(e) => e.preventDefault()}
                      >
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-filter-circle-fill ms-1" />
                        </span>
                        <input
                          type="search"
                          className="form-control ps-7 rounded-3 border-0 rounded-4"
                          placeholder="Filter Data"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </form>
                    </div>
                    <div className="mt-2 ms-4">
                      <nav aria-label="breadcrumb">
                        <ol class="breadcrumb pe-3">
                          <li class="breadcrumb-item">
                            <a href="#">Examinations</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Toppers
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <div className="p-4 bg-white rounded-4">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        {loading ? (
                          <div
                            className="spinner-border text-dark data-loader-spinner"
                            role="status"
                          ></div>
                        ) : (
                          <img src="/assets/images/folder.svg" />
                        )}

                        <p className="my-0 fw-semibold lh-1 fs-5 text-dark mx-3">
                          Toppers
                          <br />
                          <span className="text-secondary fs-6 fw-medium lh-sm">
                            0.2 MB Size
                          </span>
                        </p>
                      </div>
                      <span className="badge bg-pink rounded-3 px-4 py-2 me-1 mt-1 cursor-pointer">
                        Export
                      </span>
                    </div>

                    <div class="px-4 py-2 mt-3 bg-light rounded-4">
                      <li className="list-group-item px-0">
                        <a
                          className="d-flex align-items-center text-inherit mb-0 justify-content-between"
                          data-bs-toggle="collapse"
                          href="#specifications"
                          role="button"
                          aria-expanded="false"
                          aria-controls="specifications"
                        >
                          <p className="m-0 fw-medium lh-sm text-dark">
                            Download Report
                          </p>
                          <span className="chevron-arrow ms-4">
                            <i className="bi bi-chevron-down fs-4" />
                          </span>
                        </a>
                        <div
                          className="collapse"
                          id="specifications"
                          data-bs-parent="#ecommerceAccordion"
                        >
                          <div className="py-3">
                            <div class="rounded-4 d-flex flex-wrap">
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                All Examination Report
                              </span>
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Term Wise
                              </span>
                              <span class="btn btn-outline-primary btn-sm px-4 rounded-3 mb-1 me-1">
                                Examination Class Wise Report
                              </span>
                            </div>
                          </div>
                        </div>
                      </li>
                    </div>

                    <div class="mt-4">
                      <div className="position-relative mt-2">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("toppersTypeScroll")
                              .scrollBy({ left: -120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>
                        <div
                          id="toppersTypeScroll"
                          className="gap-1 d-flex overflow-x-auto px-6"
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {["Class", "Section", "Subject"].map((cls, idx) => (
                            <span
                              key={cls}
                              className={`btn btn-tag btn-sm ${
                                toppersType === cls
                                  ? "bg-success-subtle border border-success text-success-emphasis"
                                  : ""
                              }`}
                              onClick={() => setToppersType(cls)}
                            >
                              {cls}
                            </span>
                          ))}
                        </div>
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("toppersTypeScroll")
                              .scrollBy({ left: 120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      <div className="position-relative mt-2">
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("classScroll")
                              .scrollBy({ left: -120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-left text-white"></i>
                        </span>
                        <div
                          id="classScroll"
                          className="gap-1 d-flex overflow-x-auto px-6"
                          style={{
                            scrollBehavior: "smooth",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {classList.map((cls) => (
                            <span
                              key={cls}
                              className={`btn btn-tag btn-sm ${
                                studentClass === cls
                                  ? "bg-success-subtle border border-success text-success-emphasis"
                                  : ""
                              }`}
                              onClick={() => setStudentClass(cls)}
                            >
                              {cls}
                            </span>
                          ))}
                        </div>
                        <span
                          className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                          onClick={() =>
                            document
                              .getElementById("classScroll")
                              .scrollBy({ left: 120, behavior: "smooth" })
                          }
                        >
                          <i className="bi bi-chevron-right text-white"></i>
                        </span>
                      </div>

                      {toppersType === "Section" && (
                        <div className="position-relative mt-2">
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("sectionScroll")
                                .scrollBy({ left: -120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-left text-white"></i>
                          </span>
                          <div
                            id="sectionScroll"
                            className="gap-1 d-flex overflow-x-auto px-6"
                            style={{
                              scrollBehavior: "smooth",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {sectionList.map((sec) => (
                              <span
                                key={sec}
                                className={`btn btn-tag btn-sm ${
                                  section === sec
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSection(sec)}
                              >
                                {sec}
                              </span>
                            ))}
                          </div>
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("sectionScroll")
                                .scrollBy({ left: 120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-right text-white"></i>
                          </span>
                        </div>
                      )}

                      {toppersType === "Subject" && (
                        <div className="position-relative mt-2">
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute start-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("subjectScroll")
                                .scrollBy({ left: -120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-left text-white"></i>
                          </span>
                          <div
                            id="subjectScroll"
                            className="gap-1 d-flex overflow-x-auto px-6"
                            style={{
                              scrollBehavior: "smooth",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {subjectList.map((sec) => (
                              <span
                                key={sec}
                                className={`btn btn-tag btn-sm ${
                                  subject === sec
                                    ? "bg-success-subtle border border-success text-success-emphasis"
                                    : ""
                                }`}
                                onClick={() => setSubject(sec)}
                              >
                                {sec}
                              </span>
                            ))}
                          </div>
                          <span
                            className="circle-slider-button bg-dark rounded-circle position-absolute end-0 top-50 translate-middle-y z-1 cursor-pointer"
                            onClick={() =>
                              document
                                .getElementById("subjectScroll")
                                .scrollBy({ left: 120, behavior: "smooth" })
                            }
                          >
                            <i className="bi bi-chevron-right text-white"></i>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="my-4">
                    <nav>
                      <ul className="pagination justify-content-center mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={prevPage}>
                            <i className="bi bi-chevron-left"></i>
                          </button>
                        </li>

                        {(() => {
                          const pageNumbers = [];
                          pageNumbers.push(1);

                          if (currentPage > 3) {
                            pageNumbers.push("left-ellipsis");
                          }

                          if (currentPage !== 1 && currentPage !== totalPages) {
                            pageNumbers.push(currentPage);
                          }

                          if (currentPage < totalPages - 2) {
                            pageNumbers.push("right-ellipsis");
                          }

                          if (totalPages > 1) {
                            pageNumbers.push(totalPages);
                          }

                          return pageNumbers.map((page, index) => {
                            if (
                              page === "left-ellipsis" ||
                              page === "right-ellipsis"
                            ) {
                              return (
                                <li key={index} className="page-item disabled">
                                  <span className="page-link">...</span>
                                </li>
                              );
                            }

                            return (
                              <li
                                key={index}
                                className={`page-item ${
                                  currentPage === page ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => setCurrentPage(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            );
                          });
                        })()}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button className="page-link" onClick={nextPage}>
                            <i className="bi bi-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default Toppers;
